import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

interface ResolveIssueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (resolveNote: string) => void;
  isTransportCompanyRejection?: boolean;
  isLoading?: boolean;
}

export function ResolveIssueDialog({
  open,
  onOpenChange,
  onConfirm,
  isTransportCompanyRejection = false,
  isLoading = false,
}: ResolveIssueDialogProps) {
  const { t } = useTranslation(["fit", "common"]);
  const [resolveNote, setResolveNote] = useState("");
  const textareaId = useId();

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setResolveNote("");
    }
    onOpenChange(newOpen);
  };

  const handleConfirm = () => {
    if (!resolveNote.trim()) return;
    onConfirm(resolveNote.trim());
    setResolveNote("");
  };

  const handleCancel = () => {
    handleOpenChange(false);
  };

  const description = isTransportCompanyRejection
    ? t("fit:DescribeHowTransportCompanyRejectionWasResolved")
    : t("fit:DescribeHowBranchRejectionWasResolved");
  const isConfirmDisabled = !resolveNote.trim() || isLoading;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-125">
        <DialogHeader>
          <DialogTitle>{t("fit:FixProblem")}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor={textareaId}>{t("fit:SolutionNote")}</Label>
            <Textarea
              id={textareaId}
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
              placeholder={t("fit:DescribeTheImplementedMeasures")}
              rows={5}
              disabled={isLoading}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant={ButtonVariant.Outlined}
            colorVariant={ButtonColorVariant.Secondary}
            onClick={handleCancel}
            disabled={isLoading}
          >
            {t("common:Cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={isConfirmDisabled}>
            {isLoading ? t("fit:Saving") : t("common:Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
